import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { I18nProvider } from './i18n/index.jsx';
import './styles/global.css';
import { STALE_CHUNK_FLAG, isStaleChunkError, reloadForStaleChunk } from './utils/staleChunk';
import * as Lu from 'lucide-react';

if (import.meta.env.VITE_SENTRY_DSN) {
  const SENSITIVE = ['password', 'new_password', 'current_password', 'confirm_password', 'token', 'refreshtoken', 'refresh_token', 'credential', 'signature_data', 'secret'];
  const scrubObj = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const clone = Array.isArray(obj) ? [...obj] : { ...obj };
    for (const k of Object.keys(clone)) {
      if (SENSITIVE.some(s => k.toLowerCase().includes(s))) clone[k] = '[SCRUBBED]';
      else if (typeof clone[k] === 'object') clone[k] = scrubObj(clone[k]);
    }
    return clone;
  };
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || 'medcover-frontend@1.1.0',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'Sesión expirada',
      'Sin conexión',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event) {
      if (event.request?.data) event.request.data = scrubObj(event.request.data);
      if (event.extra) event.extra = scrubObj(event.extra);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Scrub auth headers from fetch breadcrumbs
      if (breadcrumb.data?.url && /\/auth\//.test(breadcrumb.data.url)) {
        if (breadcrumb.data.body) breadcrumb.data.body = '[SCRUBBED]';
      }
      return breadcrumb;
    },
  });
}

const FallbackUI = ({ error, resetError }) => {
  // Detectar stale chunk dentro del fallback: cuando React captura el error
  // de una lazy() import fallida, llega aquí y NO al unhandledrejection. Si es
  // ese caso, autoreload (con flag para no bucle infinito).
  if (isStaleChunkError(error) && reloadForStaleChunk()) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>
        <h1 style={{ fontSize: 20, marginBottom: 12 }}>Actualizando aplicación…</h1>
        <p style={{ color: '#666' }}>Cargando la última versión.</p>
      </div>
    );
  }
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'DM Sans', system-ui, sans-serif",
      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
    }}>
      <div style={{
        maxWidth: 520, width: '100%', textAlign: 'center',
        background: '#fff', padding: 40, borderRadius: 16,
        border: '1px solid #e2e8f0', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
          background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#DC2626',
        }}><Lu.AlertTriangle size={30} /></div>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px', color: '#111827' }}>Algo se ha roto</h1>
        <p style={{ color: '#6B7280', marginBottom: 28, lineHeight: 1.5, fontSize: 14 }}>
          La página ha tenido un error inesperado. Tus datos están a salvo. Vuelve al inicio o reinténtalo.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => { try { window.history.replaceState({}, '', '/'); } catch(_){} window.location.reload(); }} style={{
            padding: '12px 24px', background: '#2563EB', color: '#fff',
            border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>← Volver al inicio</button>
          <button onClick={resetError} style={{
            padding: '12px 24px', background: 'transparent', color: '#6B7280',
            border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>Reintentar</button>
        </div>
      </div>
    </div>
  );
};

// Stale chunk recovery (ver utils/staleChunk.js). Cubre rechazos sueltos y el
// evento propio de Vite cuando falla la precarga de un chunk.
function handleChunkError(event) {
  if (isStaleChunkError(event?.reason || event?.payload || event)) reloadForStaleChunk();
}
window.addEventListener('vite:preloadError', (event) => { event.preventDefault(); reloadForStaleChunk(); });
window.addEventListener('unhandledrejection', handleChunkError);
window.addEventListener('error', handleChunkError);
// Si el reload anterior cargó sin errores, limpiar el flag tras 30s
setTimeout(() => { try { sessionStorage.removeItem(STALE_CHUNK_FLAG); } catch (_) {} }, 30000);

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// GoogleOAuthProvider needs a clientId always; if not configured we pass '' and hide the Google button in UI.
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={FallbackUI}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <ThemeProvider>
            <I18nProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </I18nProvider>
          </ThemeProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
